import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Box, Button, Divider, Stack, Typography } from "@mui/material";
import CategoryIcon from "@mui/icons-material/Category";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { v4 as uuidv4 } from "uuid";
import ScheduleHeader from "../../../App/ScheduleHeader";
import ScheduleNotes from "../../ScheduleNotes";
import Toolbar from "../../../utils/Toolbar";
import FullscreenDialog from "../../../utils/dialog/FullscreenDialog";
import CustomDialog from "../../../utils/dialog/customDialog";
import NoDataAvailableUI from "../../../Common/NoDataAvailable";
import ComponentLoading from "../../../App/ComponentLoading";
import { exportComponentAsImage } from "../../../../utils/export/componentExporter";
import { useErrorDialog } from "../../../../contexts/ErrorDialogContext";
import PdfGenerator from "../Pdf/FaRegisterPdf";
import FaRegisterCategoryDialog from "./FaRegisterCategoryDialog";
import FaRegisterDrawer from "./FaRegisterDrawer";
import FaRegisterExcelUpload from "./FaRegisterExcelUpload";
import FaRegisterTable from "./FaRegisterTable";
import {
  ASSET_EXPENSED_OFF_CATEGORY,
  DEFAULT_CURRENT_YA,
  EXTERNAL_TYPES,
  FIELD_LABELS,
  buildDataSummaries,
  createEmptyAsset,
  deriveAsset,
  getClaimYearsFromSetting,
  getFaRegisterCaTypeSelectOptions,
  hasInitialAllowance,
  isAssetExpensedOffCategory,
  isEnhancedTwdvType,
  validateAsset,
} from "./rules";

const inputFields = [
  "assetCategory",
  "assetDescription",
  "costOfAsset",
  "yaOfPurchase",
  "caType",
  "noOfYearsClaim",
  "noOfYearsBf",
  "deferred",
  "disposal",
  "noOfYearsCf",
  "twdvBf",
  "enhancedTwdvBf",
  "iaRate",
  "ia",
  "aa",
  "twdvCf",
];

const numericFields = new Set([
  "costOfAsset",
  "yaOfPurchase",
  "noOfYearsClaim",
  "noOfYearsBf",
  "twdvBf",
  "enhancedTwdvBf",
]);

const cleanNumericInput = (value) => String(value ?? "").replace(/[^0-9]/g, "");

const getCurrentYa = (company) =>
  Number(company?.yearOfAssessment || DEFAULT_CURRENT_YA);

const assetExpensedOffInfo =
  "The asset has been expensed in P&L schedules but should still be tracked for capital allowances.";

const buildExpensedOffUploadErrorMessage = (issues, currentYa) => {
  const assetList = issues
    .slice(0, 5)
    .map(
      (issue) =>
        `Row ${issue.rowNumber}: ${issue.description} (uploaded YA ${issue.yaOfPurchase})`
    )
    .join("\n");
  const remaining =
    issues.length > 5 ? `\n...and ${issues.length - 5} more.` : "";

  return `Your assets have been uploaded successfully but we detected ${issues.length} expensed-off ${
    issues.length === 1 ? "asset" : "assets"
  } with Current YA or later.\n\n${ASSET_EXPENSED_OFF_CATEGORY} must use a YA earlier than Current YA (${currentYa}).\n\n${assetList}${remaining}\n\nWe have removed the YA of purchase value from these assets. Please review and update the YA of purchase for these assets.`;
};

const normalizeCategoryName = (value) => String(value ?? "").trim();

const getCategorySlug = (name) =>
  normalizeCategoryName(name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const getCategoryId = (category = {}) => {
  const name = normalizeCategoryName(
    category.assetCategory || category.category || category.name
  );
  if (isAssetExpensedOffCategory(name)) return ASSET_EXPENSED_OFF_CATEGORY;

  const existingId = normalizeCategoryName(
    category.assetCategoryId || category.categoryId
  );
  if (existingId) return existingId;

  return name ? `fa-register-category-${getCategorySlug(name)}` : "";
};

const normalizeAssetCategories = (categories = [], rows = []) => {
  const map = new Map();

  const addCategory = (category = {}) => {
    const assetCategory = normalizeCategoryName(
      category.assetCategory || category.category || category.name
    );
    if (isAssetExpensedOffCategory(assetCategory)) return;
    const categoryId = getCategoryId({ ...category, assetCategory });
    if (!categoryId || !assetCategory) return;

    map.set(categoryId, {
      categoryId,
      assetCategoryId: categoryId,
      assetId: categoryId,
      assetCategory,
    });
  };

  (Array.isArray(categories) ? categories : []).forEach(addCategory);
  (Array.isArray(rows) ? rows : []).forEach(addCategory);

  return [...map.values()];
};

const FaRegisterSchedule = ({
  company,
  schedule,
  scheduleDetails,
  saveSchedule,
  deleteSchedule,
  recordSaved,
  recordUnsavedData,
  recordChangeHappened,
  canEditSchedule = true,
}) => {
  const exportRef = useRef(null);
  const handleError = useErrorDialog();
  const currentYa = getCurrentYa(company);
  const caSettings = useMemo(() => schedule?.caSettings || [], [schedule]);
  const caTypeSelectOptions = useMemo(
    () => getFaRegisterCaTypeSelectOptions(caSettings),
    [caSettings]
  );

  const [loading, setLoading] = useState(true);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [assetCategories, setAssetCategories] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [activeRowKey, setActiveRowKey] = useState(null);
  const [assetDrawerOpen, setAssetDrawerOpen] = useState(false);
  const [deleteTargets, setDeleteTargets] = useState([]);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);

  const validations = useMemo(
    () =>
      Object.fromEntries(
        rows.map((row) => [
          row.rowKey,
          validateAsset(row, currentYa, caSettings),
        ])
      ),
    [rows, currentYa, caSettings]
  );

  const errorCount = useMemo(
    () =>
      Object.values(validations).reduce(
        (sum, item) => sum + Object.keys(item.errors).length,
        0
      ),
    [validations]
  );

  const validationRows = useMemo(
    () =>
      rows
        .map((row, index) => {
          const errors = validations[row.rowKey]?.errors || {};
          const errorEntries = Object.entries(errors);
          if (!errorEntries.length) return null;

          return {
            rowKey: row.rowKey,
            index,
            label:
              row.assetDescription || row.assetCategory || `Asset ${index + 1}`,
            errors: errorEntries.map(([field, message]) => ({
              field,
              label: FIELD_LABELS[field] || field,
              message,
            })),
          };
        })
        .filter(Boolean),
    [rows, validations]
  );

  const showIaColumns = rows.some((row) =>
    hasInitialAllowance(row.caType, caSettings)
  );
  const showEnhancedTwdv = rows.some((row) =>
    isEnhancedTwdvType(row.caType, caSettings)
  );
  const activeAsset = rows.find((row) => row.rowKey === activeRowKey) || null;
  const activeAssetIndex = rows.findIndex((row) => row.rowKey === activeRowKey);
  const categoryOptions = useMemo(
    () => [
      {
        group: "General",
        value: ASSET_EXPENSED_OFF_CATEGORY,
        label: ASSET_EXPENSED_OFF_CATEGORY,
        infoTitle: assetExpensedOffInfo,
      },
      ...assetCategories.map((category) => ({
        group: "Asset Categories",
        value: category.categoryId,
        label: category.assetCategory,
      })),
    ],
    [assetCategories]
  );

  const visibleFields = inputFields.filter((field) => {
    if (field === "iaRate" || field === "ia") return showIaColumns;
    if (field === "enhancedTwdvBf") return showEnhancedTwdv;
    return true;
  });
  const buildPayload = (
    nextRows = rows,
    nextNotes = notes,
    nextReviewNotes = reviewNotes,
    nextAssetCategories = assetCategories
  ) => {
    const nextSummaries = buildDataSummaries(nextRows, currentYa, caSettings);

    return {
      notes: nextNotes,
      reviewNotes: nextReviewNotes,
      assetCategories: nextAssetCategories,
      dataRows: nextRows,
      summaries: nextSummaries,
      doc: JSON.stringify({
        assetCategories: nextAssetCategories,
        assetRows: nextRows,
        summaries: nextSummaries,
        notes: nextNotes,
        reviewNotes: nextReviewNotes,
        currentYa,
      }),
    };
  };

  const markUnsaved = (
    nextRows = rows,
    nextNotes = notes,
    nextReviewNotes = reviewNotes,
    nextAssetCategories = assetCategories
  ) => {
    recordChangeHappened?.();
    recordUnsavedData?.(
      buildPayload(nextRows, nextNotes, nextReviewNotes, nextAssetCategories)
    );
  };

  useEffect(() => {
    setLoading(true);
    setNotes(schedule?.notes || "");
    setReviewNotes(schedule?.reviewNotes || "");

    const savedRows =
      scheduleDetails?.dataRows ||
      scheduleDetails?.assetRows ||
      scheduleDetails?.doc?.assetRows ||
      [];
    const savedCategories = normalizeAssetCategories(
      scheduleDetails?.assetCategories ||
        scheduleDetails?.categories ||
        scheduleDetails?.doc?.assetCategories ||
        [],
      savedRows
    );
    const categoryMap = new Map(
      savedCategories.map((category) => [category.categoryId, category])
    );

    setRows(
      savedRows.map((row) => {
        const rowKey = row.rowKey || row.assetId || uuidv4();
        const categoryId = getCategoryId(row);
        const category = categoryMap.get(categoryId);

        return deriveAsset(
          {
            ...createEmptyAsset(rowKey),
            ...row,
            rowKey,
            assetCategoryId: categoryId,
            assetCategory: category?.assetCategory || row.assetCategory || "",
          },
          currentYa,
          caSettings
        );
      })
    );
    setAssetCategories(savedCategories);
    setSelectedRows(new Set());
    setActiveRowKey(null);
    setAssetDrawerOpen(false);
    setSaveAttempted(false);
    setLoading(false);
  }, [schedule, scheduleDetails, currentYa, caSettings]);

  const handleExportAsImage = async (e) => {
    e.stopPropagation();
    if (exportRef.current) {
      await exportComponentAsImage(exportRef.current, "FA Register");
    }
  };

  const createCategory = (name) => {
    const assetCategory = normalizeCategoryName(name);
    if (!assetCategory) {
      return { error: "Enter a category name." };
    }

    if (isAssetExpensedOffCategory(assetCategory)) {
      return {
        error:
          "Asset expensed off is a system defined category. Select it from the asset category dropdown instead.",
      };
    }

    const existing = assetCategories.find(
      (category) =>
        category.assetCategory.trim().toLowerCase() ===
        assetCategory.toLowerCase()
    );

    if (existing) {
      return {
        error: "This category already exists.",
        categoryId: existing.categoryId,
        assetCategory: existing.assetCategory,
      };
    }

    const categoryId = uuidv4();
    const nextCategories = [
      ...assetCategories,
      {
        categoryId,
        assetCategoryId: categoryId,
        assetId: categoryId,
        assetCategory,
      },
    ];

    setAssetCategories(nextCategories);
    markUnsaved(rows, notes, reviewNotes, nextCategories);
    return { categoryId, assetCategory, categories: nextCategories };
  };

  const handleAddCategory = () => {
    const result = createCategory(newCategoryName);
    if (result.error) {
      setCategoryError(result.error);
      return;
    }

    setNewCategoryName("");
    setCategoryError("");
  };

  const handleQuickAddCategory = (name, rowKey) => {
    const result = createCategory(name);
    if (!result.categoryId) {
      handleError(result.error || "Unable to add category.");
      return;
    }

    if (!rowKey) return;

    const nextRows = rows.map((row) => {
      if (row.rowKey !== rowKey) return row;
      return deriveAsset(
        {
          ...row,
          assetCategoryId: result.categoryId,
          assetCategory: result.assetCategory,
        },
        currentYa,
        caSettings
      );
    });

    setRows(nextRows);
    markUnsaved(
      nextRows,
      notes,
      reviewNotes,
      result.categories || assetCategories
    );
    setSaveAttempted(false);
  };

  const handleCloseCategoryDialog = () => {
    setCategoryDialogOpen(false);
    setCategoryError("");
    setNewCategoryName("");
  };

  const handleRemoveCategory = (categoryId) => {
    const isUsed = rows.some((row) => row.assetCategoryId === categoryId);
    if (isUsed) {
      handleError("This category is used by an asset and cannot be removed.");
      return;
    }

    const nextCategories = assetCategories.filter(
      (category) => category.categoryId !== categoryId
    );
    setAssetCategories(nextCategories);
    markUnsaved(rows, notes, reviewNotes, nextCategories);
  };

  const addRow = () => {
    const defaultCategory = assetCategories[0];
    const rowKey = uuidv4();
    const initialAsset = {
      ...createEmptyAsset(rowKey),
      ...(defaultCategory
        ? {
            assetCategoryId: defaultCategory.categoryId,
            assetCategory: defaultCategory.assetCategory,
          }
        : {}),
    };
    const nextRows = [
      ...rows,
      deriveAsset(initialAsset, currentYa, caSettings),
    ];
    setRows(nextRows);
    setActiveRowKey(rowKey);
    setAssetDrawerOpen(true);
    markUnsaved(nextRows);
    setSaveAttempted(false);
  };

  const handleOpenExcelUploadDialog = () => setExcelDialogOpen(true);
  const handleCloseExcelUploadDialog = () => setExcelDialogOpen(false);

  const normalizeUploadedRowsWithCategories = (uploadedRows = []) => {
    const categoryMap = new Map(
      assetCategories.map((category) => [
        normalizeCategoryName(category.assetCategory).toLowerCase(),
        category,
      ])
    );
    const nextCategories = [...assetCategories];

    const getUploadedCategory = (name) => {
      const assetCategory = normalizeCategoryName(name);
      if (!assetCategory || isAssetExpensedOffCategory(assetCategory)) {
        return null;
      }

      const key = assetCategory.toLowerCase();
      const existing = categoryMap.get(key);
      if (existing) return existing;

      const categoryId = uuidv4();
      const newCategory = {
        categoryId,
        assetCategoryId: categoryId,
        assetId: categoryId,
        assetCategory,
      };

      categoryMap.set(key, newCategory);
      nextCategories.push(newCategory);
      return newCategory;
    };

    const nextRows = uploadedRows.map((row) => {
      const category = getUploadedCategory(row.assetCategory);
      if (!category) return row;

      return deriveAsset(
        {
          ...row,
          assetCategoryId: category.categoryId,
          assetCategory: category.assetCategory,
        },
        currentYa,
        caSettings
      );
    });

    return { rows: nextRows, categories: nextCategories };
  };

  const onExcelAddRows = (uploadedRows, uploadContext = {}) => {
    const expensedOffCurrentYaIssues =
      uploadContext.expensedOffCurrentYaIssues || [];

    if (!uploadedRows.length) return;

    const { rows: normalizedUploadedRows, categories: nextCategories } =
      normalizeUploadedRowsWithCategories(uploadedRows);
    const nextRows = [...rows, ...normalizedUploadedRows];
    setRows(nextRows);
    setAssetCategories(nextCategories);
    setSelectedRows(new Set());

    markUnsaved(nextRows, notes, reviewNotes, nextCategories);
    setSaveAttempted(false);

    if (expensedOffCurrentYaIssues.length > 0) {
      handleError(
        buildExpensedOffUploadErrorMessage(
          expensedOffCurrentYaIssues,
          currentYa
        )
      );
    } else {
      setActiveRowKey(normalizedUploadedRows[0].rowKey);
      setAssetDrawerOpen(true);
    }

    handleCloseExcelUploadDialog();
  };

  const openAsset = useCallback((rowKey) => {
    setActiveRowKey(rowKey);
    setAssetDrawerOpen(true);
  }, []);

  const goToAsset = useCallback(
    (index) => {
      if (index < 0 || index >= rows.length) return;
      openAsset(rows[index].rowKey);
    },
    [openAsset, rows]
  );

  const navigateAsset = useCallback(
    (direction) => {
      if (activeAssetIndex < 0) return;
      goToAsset(activeAssetIndex + direction);
    },
    [activeAssetIndex, goToAsset]
  );

  const duplicateActiveAsset = () => {
    if (!activeAsset || !canEditSchedule) return;

    const rowKey = uuidv4();
    const copiedAsset = {
      ...activeAsset,
      rowKey,
      assetDescription: activeAsset.assetDescription
        ? `${activeAsset.assetDescription} (Copy)`
        : "",
    };
    delete copiedAsset.assetId;

    const insertAt = activeAssetIndex >= 0 ? activeAssetIndex + 1 : rows.length;
    const nextRows = [
      ...rows.slice(0, insertAt),
      deriveAsset(copiedAsset, currentYa, caSettings),
      ...rows.slice(insertAt),
    ];

    setRows(nextRows);
    setActiveRowKey(rowKey);
    setAssetDrawerOpen(true);
    markUnsaved(nextRows);
    setSaveAttempted(false);
  };

  const handleInputChange = (rowKey, field, value) => {
    const nextRows = rows.map((row) => {
      if (row.rowKey !== rowKey) return row;

      let changed = {
        ...row,
        [field]: numericFields.has(field) ? cleanNumericInput(value) : value,
      };

      if (field === "assetCategory") {
        if (isAssetExpensedOffCategory(value)) {
          changed = {
            ...changed,
            assetCategoryId: ASSET_EXPENSED_OFF_CATEGORY,
            assetCategory: ASSET_EXPENSED_OFF_CATEGORY,
            isPLScheduleAsset: true,
          };

          if (Number(changed.yaOfPurchase) >= Number(currentYa)) {
            changed.yaOfPurchase = "";
            changed.noOfYearsBf = "";
            changed.twdvBf = "";
          }
        } else {
          const category = assetCategories.find(
            (item) => item.categoryId === value
          );
          changed = {
            ...changed,
            assetCategoryId: category?.categoryId || "",
            assetCategory: category?.assetCategory || "",
            isPLScheduleAsset: false,
          };
        }
      }

      if (field === "caType") {
        const presetClaimYears = getClaimYearsFromSetting(value, caSettings);
        const isExternalType = EXTERNAL_TYPES.has(value);

        changed = {
          ...changed,
          noOfYearsClaim: presetClaimYears ?? "",
          noOfYearsBf: "",
          twdvBf: "",
          enhancedTwdvBf: isEnhancedTwdvType(value, caSettings) ? "" : null,
          deferred: EXTERNAL_TYPES.has(value) ? null : false,
          isPLScheduleAsset: isExternalType ? false : changed.isPLScheduleAsset,
          disposal: isExternalType ? false : changed.disposal,
        };

        if (
          isExternalType &&
          isAssetExpensedOffCategory(changed.assetCategory)
        ) {
          changed.assetCategoryId = "";
          changed.assetCategory = "";
        }
      }

      if (field === "yaOfPurchase") {
        changed.noOfYearsBf = "";
        changed.twdvBf = "";

        if (
          isAssetExpensedOffCategory(changed.assetCategory) &&
          Number(changed.yaOfPurchase) >= Number(currentYa)
        ) {
          changed.yaOfPurchase = "";
        }
      }

      return deriveAsset(changed, currentYa, caSettings);
    });

    setRows(nextRows);
    markUnsaved(nextRows);
  };

  const handleSelectRow = (rowKey, checked) => {
    setSelectedRows((current) => {
      const next = new Set(current);
      if (checked) next.add(rowKey);
      else next.delete(rowKey);
      return next;
    });
  };

  const handleSelectAll = (checked) => {
    setSelectedRows(
      checked ? new Set(rows.map((row) => row.rowKey)) : new Set()
    );
  };

  const confirmDeleteRows = () => {
    const targetSet = new Set(deleteTargets);
    const nextRows = rows.filter((row) => !targetSet.has(row.rowKey));
    setRows(nextRows);
    setSelectedRows((current) => {
      const next = new Set(current);
      deleteTargets.forEach((rowKey) => next.delete(rowKey));
      return next;
    });
    if (targetSet.has(activeRowKey)) {
      setActiveRowKey(null);
      setAssetDrawerOpen(false);
    }
    setDeleteTargets([]);
    markUnsaved(nextRows);
  };

  const handleNotesChange = (value) => {
    setNotes(value);
    markUnsaved(rows, value, reviewNotes);
  };

  const handleReviewNotesChange = (value) => {
    setReviewNotes(value);
    markUnsaved(rows, notes, value);
  };

  const handleSave = async () => {
    setSaveAttempted(true);

    if (errorCount > 0) {
      const affectedAssets = validationRows
        .slice(0, 3)
        .map((item) => `${item.index + 1}. ${item.label}`)
        .join("; ");
      const moreRowsText =
        validationRows.length > 3
          ? `, plus ${validationRows.length - 3} more`
          : "";

      handleError(
        `Schedule not saved. Resolve ${errorCount} ${
          errorCount === 1 ? "issue" : "issues"
        } across ${validationRows.length} ${
          validationRows.length === 1 ? "asset" : "assets"
        }: ${affectedAssets}${moreRowsText}.`
      );
      return false;
    }

    const saved = await saveSchedule(buildPayload());
    if (saved) {
      recordSaved?.();
      return true;
    }

    return false;
  };

  const renderMainContent = () => (
    <Box sx={{ width: "100%" }}>
      <Toolbar
        title={`Schedule ${schedule.scheduleNo}`}
        titleInfo={`This schedule allows you to record and track your fixed assets on an asset-by-asset basis. All asset here will be flowed into respective capital allowance schedules automatically.`}
        subtitle={schedule.type}
        selectedCount={selectedRows.size}
        showSelectedCount
        totalCount={rows.length}
        onRemove={() => setDeleteTargets([...selectedRows])}
        onAdd={addRow}
        fullscreenOpen={fullscreenOpen}
        onToggleFullscreen={() => setFullscreenOpen(!fullscreenOpen)}
        additionalControls={
          canEditSchedule ? (
            <Button
              size='small'
              startIcon={<CategoryIcon />}
              onClick={() => setCategoryDialogOpen(true)}
            >
              Categories
            </Button>
          ) : null
        }
        allowSticky
        size='medium'
        handleExportAsImage={handleExportAsImage}
        exportRef={exportRef}
        canEdit={canEditSchedule}
      />

      {saveAttempted && errorCount > 0 && (
        <Alert severity='error' sx={{ mb: 2 }}>
          <Stack spacing={1}>
            <Typography variant='subtitle2' sx={{ fontWeight: 800 }}>
              Resolve {errorCount} {errorCount === 1 ? "issue" : "issues"}{" "}
              across {validationRows.length}{" "}
              {validationRows.length === 1 ? "asset" : "assets"} before saving.
            </Typography>
            <Stack spacing={0.75}>
              {validationRows.slice(0, 5).map((item) => (
                <Stack
                  key={item.rowKey}
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent='space-between'
                  spacing={1}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: "rgba(255,255,255,0.65)",
                  }}
                >
                  <Box>
                    <Typography variant='body2' sx={{ fontWeight: 700 }}>
                      {item.index + 1}. {item.label}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {item.errors
                        .map((error) => `${error.label}: ${error.message}`)
                        .join(" ")}
                    </Typography>
                  </Box>
                  <Button
                    size='small'
                    variant='outlined'
                    color='error'
                    onClick={() => openAsset(item.rowKey)}
                    sx={{ textTransform: "none", flexShrink: 0 }}
                  >
                    View issues
                  </Button>
                </Stack>
              ))}
            </Stack>
            {validationRows.length > 5 && (
              <Typography variant='caption' color='text.secondary'>
                Showing first 5 affected assets. Remaining rows are marked in
                the table.
              </Typography>
            )}
          </Stack>
        </Alert>
      )}

      {rows.length > 0 ? (
        <FaRegisterTable
          exportRef={exportRef}
          rows={rows}
          selectedRows={selectedRows}
          activeRowKey={activeRowKey}
          fullscreenOpen={fullscreenOpen}
          canEditSchedule={canEditSchedule}
          visibleFields={visibleFields}
          caSettings={caSettings}
          onSelectRow={handleSelectRow}
          onSelectAll={handleSelectAll}
          onOpenAsset={openAsset}
          onDeleteAsset={(rowKey) => setDeleteTargets([rowKey])}
          saveAttempted={saveAttempted}
          validationRows={validationRows}
        />
      ) : (
        <NoDataAvailableUI
          icon={Inventory2OutlinedIcon}
          title='No Assets Defined'
          content='Add the first asset to start building the fixed asset register.'
          subContent='Capital allowance calculations will update as asset details are entered.'
          onAddNew={canEditSchedule ? addRow : undefined}
        />
      )}

      <FaRegisterDrawer
        open={assetDrawerOpen}
        rows={rows}
        activeAsset={activeAsset}
        activeAssetIndex={activeAssetIndex}
        visibleFields={visibleFields}
        validations={validations}
        saveAttempted={saveAttempted}
        currentYa={currentYa}
        caSettings={caSettings}
        categoryOptions={categoryOptions}
        caTypeSelectOptions={caTypeSelectOptions}
        canEditSchedule={canEditSchedule}
        onClose={() => setAssetDrawerOpen(false)}
        onQuickAddCategory={handleQuickAddCategory}
        onInputChange={handleInputChange}
        onNavigateAsset={navigateAsset}
        onDuplicateAsset={duplicateActiveAsset}
        onDeleteAsset={(rowKey) => setDeleteTargets([rowKey])}
      />
    </Box>
  );

  if (loading) return <ComponentLoading />;

  return (
    <Box sx={{ padding: 4 }}>
      <ScheduleHeader
        title={schedule.type}
        companyName={company.name}
        uen={company.uen}
        yearOfAssessment={company.yearOfAssessment}
        financialYearStart={company.financialYearStart}
        financialYearEnd={company.financialYearEnd}
        deleteSchedule={deleteSchedule}
        handleSave={handleSave}
        handleExcel={handleOpenExcelUploadDialog}
        canEditSchedule={canEditSchedule}
        canUseAi
        renderAiType={"fa_register"}
      >
        <PdfGenerator
          schedule={schedule}
          company={company}
          assetRows={rows}
          notes={notes}
          reviewNotes={reviewNotes}
        />
      </ScheduleHeader>
      <br />

      {!fullscreenOpen && renderMainContent()}

      <Divider sx={{ my: 4 }} />
      <ScheduleNotes
        notes={notes}
        setNotes={handleNotesChange}
        reviewNotes={reviewNotes}
        setReviewNotes={handleReviewNotesChange}
        enableEdit={canEditSchedule}
      />

      <FaRegisterExcelUpload
        currentYa={currentYa}
        caSettings={caSettings}
        categoryOptions={categoryOptions}
        onExcelAddRows={onExcelAddRows}
        open={excelDialogOpen}
        handleCloseDialog={handleCloseExcelUploadDialog}
      />

      <FullscreenDialog
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        title={schedule.type}
        subtitle={`${company.name} (${company.uen}) - YA ${company.yearOfAssessment}`}
      >
        {renderMainContent()}
      </FullscreenDialog>

      <FaRegisterCategoryDialog
        open={categoryDialogOpen}
        onClose={handleCloseCategoryDialog}
        assetCategories={assetCategories}
        rows={rows}
        newCategoryName={newCategoryName}
        categoryError={categoryError}
        onCategoryNameChange={(value) => {
          setNewCategoryName(value);
          setCategoryError("");
        }}
        onAddCategory={handleAddCategory}
        onRemoveCategory={handleRemoveCategory}
      />

      <CustomDialog
        open={deleteTargets.length > 0}
        onClose={() => setDeleteTargets([])}
        onConfirm={confirmDeleteRows}
        title='Delete selected assets?'
        description={`Are you sure you want to delete ${
          deleteTargets.length === 1
            ? "this asset"
            : `${deleteTargets.length} assets`
        }? This action cannot be undone.`}
        type='error'
      />
    </Box>
  );
};

export default FaRegisterSchedule;
